/** Biểu đồ SVG và định dạng số của bản mẫu (`stackedCols`, `hbarsHTML`, `legendHTML`, `heatClass`…). */
import type { ReactNode } from "react";

/** Nhóm việc của giờ học, cùng thứ tự và màu (`f{c}`/`b{c}`) với bản mẫu. */
export const ACT = [
  { id: "test", name: "Đề thi", c: 1, ic: "📝" },
  { id: "part", name: "Luyện theo part", c: 2, ic: "🎯" },
  { id: "gram", name: "Ngữ pháp", c: 6, ic: "📖" },
  { id: "vocab", name: "Từ vựng", c: 3, ic: "📗" },
  { id: "fix", name: "Trị lỗi", c: 4, ic: "📕" },
  { id: "dict", name: "Nghe & chép", c: 5, ic: "🎧" },
  { id: "other", name: "Khác", c: 0, ic: "🗒️" },
] as const;
export type ActId = (typeof ACT)[number]["id"];
export type Parts = Partial<Record<string, number>>;

export const sum = (o: Parts) => Object.values(o).reduce<number>((a, b) => a + (b ?? 0), 0);
export const fmtMin = (m: number) => {
  m = Math.round(m);
  if (m < 60) return `${m} phút`;
  const h = Math.floor(m / 60),
    r = m % 60;
  return `${h} giờ${r ? ` ${r} phút` : ""}`;
};
export const fmtShort = (m: number) => {
  m = Math.round(m);
  if (m < 60) return `${m}p`;
  const h = Math.floor(m / 60),
    r = m % 60;
  return `${h}g${r ? ` ${String(r).padStart(2, "0")}p` : ""}`;
};
export const mmss = (s: number) => {
  s = Math.max(0, Math.round(s));
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
};
export function tipFor(title: string, parts: Parts) {
  const rows = ACT.filter((a) => (parts[a.id] ?? 0) > 0).map(
    (a) => `#${a.c} ${a.name}: ${Math.round(parts[a.id] ?? 0)} phút`,
  );
  return [`${title} · ${fmtMin(sum(parts))}`, ...(rows.length ? rows : ["Chưa học"])].join("|");
}
export function presentActs(list: Parts[]) {
  return ACT.filter((a) => list.some((p) => (p[a.id] ?? 0) > 0)).map((a) => a.id);
}

function roundTop(x: number, y: number, w: number, h: number, r: number) {
  r = Math.max(0, Math.min(r, h, w / 2));
  return `M${x},${y + h} L${x},${y + r} Q${x},${y} ${x + r},${y} L${x + w - r},${y} Q${x + w},${y} ${x + w},${y + r} L${x + w},${y + h} Z`;
}
export interface Col {
  label: string;
  parts: Parts;
  tip?: string;
  val?: string;
  strong?: boolean;
  future?: boolean;
}
/** Cột chồng theo nhóm việc (phút), có lưới, nhãn và đường mục tiêu. */
export function StackedCols({
  cols,
  W = 720,
  H = 236,
  bw: maxBw = 24,
  step = 20,
  goal,
  goalLbl = "",
  labelEvery,
  aria,
}: {
  cols: Col[];
  W?: number;
  H?: number;
  bw?: number;
  step?: number;
  goal?: number;
  goalLbl?: string;
  labelEvery?: number;
  aria: string;
}) {
  const L = 36,
    R = 12,
    T = 22,
    B = 30;
  const pw = W - L - R,
    ph = H - T - B,
    slot = pw / cols.length,
    bw = Math.min(maxBw, slot * 0.62);
  const totals = cols.map((c) => sum(c.parts));
  const yMax = Math.max(step, Math.ceil(Math.max(...totals, goal ?? 0, 1) / step) * step);
  const y = (v: number) => T + ph - (v / yMax) * ph;
  const grid: ReactNode[] = [];
  for (let v = 0; v <= yMax; v += step)
    grid.push(
      <g key={`g${v}`}>
        <line className={v === 0 ? "c-base" : "c-grid"} x1={L} x2={W - R} y1={y(v)} y2={y(v)} />
        <text className="c-lbl" x={L - 8} y={y(v) + 4} textAnchor="end">
          {v}
        </text>
      </g>,
    );
  return (
    <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={aria}>
      {grid}
      {cols.map((c, i) => {
        const cx = L + slot * i + slot / 2,
          x = cx - bw / 2;
        let cum = 0;
        const segs = ACT.filter((a) => (c.parts[a.id] ?? 0) > 0);
        const showLbl = labelEvery ? i % labelEvery === 0 || c.strong : true;
        const total = totals[i] ?? 0;
        return (
          <g key={c.label + String(i)}>
            <g className="colg mark">
              {segs.map((a, k) => {
                const v = c.parts[a.id] ?? 0,
                  y0 = y(cum),
                  y1 = y(cum + v);
                cum += v;
                const h = y0 - y1;
                return k === segs.length - 1 ? (
                  <path key={a.id} className={`f${a.c}`} d={roundTop(x, y1, bw, h, 4)} />
                ) : (
                  <rect
                    key={a.id}
                    className={`f${a.c}`}
                    x={x}
                    y={y1 + 2}
                    width={bw}
                    height={Math.max(0, h - 2)}
                  />
                );
              })}
              {c.future && (
                <text className="c-lbl" x={cx} y={y(0) - 8} textAnchor="middle">
                  ·
                </text>
              )}
              <rect
                className="col-hit"
                x={L + slot * i}
                y={T}
                width={slot}
                height={ph}
                tabIndex={0}
                data-tip={c.tip ?? ""}
              />
            </g>
            {showLbl && (
              <text
                className={`c-lbl ${c.strong ? "strong" : ""}`}
                x={cx}
                y={H - 9}
                textAnchor="middle"
              >
                {c.label}
              </text>
            )}
            {c.val && total > 0 && (
              <text className="c-val" x={cx} y={y(total) - 7} textAnchor="middle">
                {c.val}
              </text>
            )}
          </g>
        );
      })}
      {goal ? (
        <>
          <line className="c-goal" x1={L} x2={W - R} y1={y(goal)} y2={y(goal)} />
          <text className="c-goal-lbl" x={W - R} y={y(goal) - 6} textAnchor="end">
            {goalLbl}
          </text>
        </>
      ) : null}
    </svg>
  );
}
export function Legend({ ids }: { ids?: readonly string[] }) {
  return (
    <div className="legend">
      {ACT.filter((a) => !ids || ids.includes(a.id)).map((a) => (
        <span key={a.id}>
          <i className={`b${a.c}`} />
          {a.name}
        </span>
      ))}
    </div>
  );
}
/** Thanh ngang "Hôm nay học gì?": phút theo nhóm việc, xếp nhiều → ít. */
export function HBars({ totals }: { totals: Parts }) {
  const all = sum(totals) || 1,
    mx = Math.max(...Object.values(totals).map((v) => v ?? 0), 1);
  return (
    <>
      {ACT.filter((a) => (totals[a.id] ?? 0) > 0)
        .sort((a, b) => (totals[b.id] ?? 0) - (totals[a.id] ?? 0))
        .map((a) => {
          const v = totals[a.id] ?? 0;
          return (
            <div
              key={a.id}
              className="hbar"
              data-tip={`${a.name} · ${fmtMin(v)}|${Math.round((v / all) * 100)}% thời gian học`}
            >
              <span className="hl">
                <i className={`b${a.c}`} />
                {a.name}
              </span>
              <span className="ht">
                <i className={`b${a.c}`} style={{ width: `${((v / mx) * 100).toFixed(1)}%` }} />
              </span>
              <span className="hv">
                {fmtShort(v)} <small>· {Math.round((v / all) * 100)}%</small>
              </span>
            </div>
          );
        })}
    </>
  );
}
/** Màu ô lịch theo phút học trong ngày; `null` là ngày chưa tới. */
export function heatClass(v: number | null) {
  if (v === null) return "fut";
  if (v <= 0) return "h0 miss";
  if (v < 30) return "h1";
  if (v < 60) return "h2";
  if (v < 90) return "h3";
  if (v < 120) return "h4";
  return "h5";
}
export function HeatLegend({ miss = true }: { miss?: boolean }) {
  return (
    <div className="heat-legend">
      ít <i className="h1" />
      <i className="h2" />
      <i className="h3" />
      <i className="h4" />
      <i className="h5" /> nhiều
      {miss && (
        <span style={{ marginLeft: 10, display: "inline-flex", alignItems: "center", gap: 5 }}>
          <i className="h0" style={{ position: "relative" }} />
          bỏ lỡ (chấm đỏ)
        </span>
      )}
    </div>
  );
}
